import { useEffect, useState } from 'react';
import { fetchSiteConditionState } from '../domain/continuousSafety/siteConditionState';
import {
  selectShowcaseForkliftZone,
  showcaseForkliftOverlayPolygon,
} from '../domain/showcase/showcasePresentation';
import { api } from '../services/api';
import type { Zone } from '../services/zonesApi';

export type ShowcaseSpatialZoneOverlay = {
  zoneId: string | null;
  polygon: number[][] | null;
};

const emptyOverlay = (): ShowcaseSpatialZoneOverlay => ({ zoneId: null, polygon: null });

function text(value: unknown): string {
  return String(value || '').trim();
}

function zoneRows(payload: unknown): Array<Partial<Zone>> {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as { zones?: unknown; items?: unknown };
    if (Array.isArray(record.zones)) return record.zones;
    if (Array.isArray(record.items)) return record.items;
  }
  return [];
}

export function useShowcaseSpatialZoneOverlay(args: {
  active: boolean;
  siteId?: string | null;
  cameraId?: string | null;
  conditionId?: string | null;
  zoneId?: string | null;
  excludeZoneId?: string | null;
}): ShowcaseSpatialZoneOverlay {
  const [overlay, setOverlay] = useState<ShowcaseSpatialZoneOverlay>(emptyOverlay);
  const siteId = text(args.siteId);
  const cameraId = text(args.cameraId);
  const conditionId = text(args.conditionId);
  const zoneId = text(args.zoneId);
  const excludeZoneId = text(args.excludeZoneId);

  useEffect(() => {
    if (!args.active || !cameraId) {
      setOverlay(emptyOverlay());
      return undefined;
    }

    let cancelled = false;
    const resolve = async () => {
      let resolvedZoneId = zoneId;
      if (!resolvedZoneId && conditionId && siteId) {
        try {
          const result = await fetchSiteConditionState({ siteId, identityOnly: true });
          if (result.status === 'success') {
            const match = result.data.conditions.find((item) => text(item.conditionId) === conditionId);
            resolvedZoneId = text(
              match?.canonicalScope?.zoneIds?.[0]
              || match?.measurementDefinition?.gateZoneId,
            );
            if (excludeZoneId && resolvedZoneId === excludeZoneId) resolvedZoneId = '';
          }
        } catch {
          resolvedZoneId = zoneId;
        }
      }

      let zones: Array<Partial<Zone>> = [];
      try {
        const response = await api.get('/api/v1/zones', { params: { camera_id: cameraId } });
        zones = zoneRows(response?.data);
      } catch {
        zones = [];
      }
      if (cancelled) return;
      const selected = selectShowcaseForkliftZone({
        zones,
        cameraId,
        zoneId: resolvedZoneId || null,
        excludeZoneIds: [excludeZoneId],
      });
      const polygon = showcaseForkliftOverlayPolygon(selected);
      setOverlay({
        zoneId: text(selected?.zone_id || selected?.id) || null,
        polygon,
      });
    };

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [args.active, siteId, cameraId, conditionId, zoneId, excludeZoneId]);

  return overlay;
}
